import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { PawPrint, Home, Plus, AlertCircle, FileText, Phone, Users, Menu, X, BookOpen, Shield } from 'lucide-react';
import { useAdmin } from '../context/AdminContext';

const Layout = ({ children }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAdmin } = useAdmin();
  
  const isActive = (path) => location.pathname === path;
  
  const navLinks = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/incidents', label: 'Incidents', icon: AlertCircle },
    { path: '/feeding', label: 'Feeding', icon: Users },
    { path: '/emergency', label: 'Emergency', icon: Phone },
    { path: '/knowledge', label: 'Knowledge', icon: BookOpen },
    { path: '/policy', label: 'Policy', icon: FileText },
  ];
  
  return (
    <div className="min-h-screen bg-background">
      <header className="bg-white border-b-2 border-orange-100 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-testid="logo-link">
              <div className="bg-primary p-2 rounded-xl shadow-lg shadow-orange-500/30">
                <PawPrint className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl font-extrabold text-text-primary font-heading">
                AnimalCare <span className="text-primary">OW</span>
              </h1>
            </Link>
            
            <nav className="hidden md:flex gap-2">
              {navLinks.map(link => {
                const Icon = link.icon;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`nav-${link.label.toLowerCase()}-link`}
                    className={`px-4 py-2 rounded-full font-bold transition-all duration-300 flex items-center gap-2 ${
                      isActive(link.path)
                        ? 'bg-primary text-white shadow-lg shadow-orange-500/30'
                        : 'bg-white text-primary border-2 border-orange-100 hover:border-primary hover:bg-orange-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {link.label}
                  </Link>
                );
              })}
              <Link
                to="/add"
                data-testid="nav-add-link"
                className="px-6 py-2 rounded-full font-bold transition-all duration-300 bg-secondary text-white shadow-lg shadow-sky-500/30 hover:shadow-sky-500/50 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Animal
              </Link>
              {isAdmin && (
                <Link
                  to="/admin"
                  data-testid="nav-admin-link"
                  className="px-6 py-2 rounded-full font-bold transition-all duration-300 bg-purple-500 text-white shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 flex items-center gap-2"
                >
                  <Shield className="w-4 h-4" />
                  Admin
                </Link>
              )}
            </nav>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-xl text-primary hover:bg-orange-50 transition-all"
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden py-4 space-y-2 border-t border-orange-100">
              {navLinks.map(link => {
                const Icon = link.icon;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      isActive(link.path)
                        ? 'bg-primary text-white'
                        : 'text-primary hover:bg-orange-50'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-bold">{link.label}</span>
                  </Link>
                );
              })}
              <Link
                to="/add"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-add"
                className="flex items-center gap-3 px-4 py-3 rounded-xl bg-secondary text-white transition-all"
              >
                <Plus className="w-5 h-5" />
                <span className="font-bold">Add Animal</span>
              </Link>
            </div>
          )}
        </div>
      </header>

      <main className="pb-8">{children}</main>
    </div>
  );
};

export default Layout;